import axios, {AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import {datadogLogs} from "@datadog/browser-logs";
import {v4 as uuidv4} from 'uuid';
import {retrieveLaunchParams} from "@telegram-apps/sdk-react";
import ct from 'countries-and-timezones'

async function getAvailableHost(): Promise<string> {
  const PRIMARY = import.meta.env.VITE_API_HOST;
  const SECONDARY = import.meta.env.VITE_API_HOST_SECONDARY;

  async function checkHost(host: string, signal?: AbortSignal): Promise<void> {
    const response = await axios.get<{alive?: unknown}>(`${host}api/health`, {
      timeout: 30000,
      signal,
    });

    if (response.status !== 200 || response.data?.alive !== true) {
      throw new Error(`Invalid API health response from ${host}`);
    }
  }

  if (!SECONDARY) {
    await checkHost(PRIMARY);
    return PRIMARY;
  }

  const hosts = [PRIMARY, SECONDARY];
  const controllers = hosts.map(() => new AbortController());
  const primaryGraceMs = 1000;

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let primaryFailed = false;
    let secondaryFailed = false;
    let secondarySucceeded = false;
    let graceTimeout: ReturnType<typeof setTimeout> | undefined;

    const chooseHost = (index: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(graceTimeout);
      resolve(hosts[index]);
      controllers[1 - index].abort();
    };

    const failIfBothUnavailable = () => {
      if (primaryFailed && secondaryFailed) {
        settled = true;
        reject(new Error('No API hosts available'));
      }
    };

    hosts.forEach((host, index) => {
      checkHost(host, controllers[index].signal).then(() => {
        if (settled) return;

        if (index === 0) {
          chooseHost(0);
        } else if (primaryFailed) {
          chooseHost(1);
        } else {
          secondarySucceeded = true;
          graceTimeout = setTimeout(() => chooseHost(1), primaryGraceMs);
        }
      }).catch(() => {
        if (settled) return;

        if (index === 0) {
          primaryFailed = true;
          if (secondarySucceeded) chooseHost(1);
        } else {
          secondaryFailed = true;
        }
        failIfBothUnavailable();
      });
    });
  });
}

let hostSelection: Promise<string> | undefined;

function getSelectedHost(): Promise<string> {
  if (!hostSelection) {
    hostSelection = getAvailableHost()
      .then((host) => {
        datadogLogs.logger.info("Host selected", {selectedApiHost: host});
        return host;
      })
      .catch((error) => {
        hostSelection = undefined;
        throw error;
      });
  }

  return hostSelection;
}

const TIMEOUT = 30000;

const getAuthToken = (): string | null => {
  function base64encode(str: string): string {
    const encode = encodeURIComponent(str).replace(/%([a-f0-9]{2})/gi, (_m, $1) => String.fromCharCode(parseInt($1, 16)))
    return btoa(encode)
  }
  const lp = retrieveLaunchParams();
  return base64encode(lp.initDataRaw ? lp.initDataRaw : '');
};

const getStartParam = (): string => {
  const lp = retrieveLaunchParams();
  return lp.initData?.startParam || "";
}

const logError = (error: AxiosError) => {
  datadogLogs.logger.error("API Error:", {
    message: error.message,
    url: error.config?.url,
    method: error.config?.method,
    requestBody: error.config?.data,
    status: error.response?.status,
    response: error.response?.data,
    axios_error: {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack,
      is_aborted: axios.isCancel(error) || Boolean(error.config?.signal?.aborted),
      timeout_ms: error.config?.timeout,
      response_status: error.response?.status,
      response_status_text: error.response?.statusText,
      response_data: error.response?.data,
      request_status: error.request?.status,
      request_ready_state: error.request?.readyState,
      cause: error.cause instanceof Error
        ? {name: error.cause.name, message: error.cause.message, stack: error.cause.stack}
        : error.cause,
    },
  }, error);
};

const retryRequest = async (error: AxiosError, retries: number = 3): Promise<AxiosResponse | null> => {
  if (!error.config) {
    throw error;
  }
  let attempt = 0;
  while (attempt < retries) {
    attempt += 1;
    try {
      datadogLogs.logger.warn(`Retry attempt ${attempt}`, {url: error.config.url});
      return await axios.request(error.config);
    } catch (retryError) {
      if (attempt >= retries) {
        if (retryError instanceof AxiosError) {
          logError(retryError);
        }
        throw retryError;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
  return null;
};

const apiClient: AxiosInstance = axios.create({
  timeout: TIMEOUT,
});

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const host = await getSelectedHost();
    config.baseURL = host;
    const token = getAuthToken();
    if (token) {
      config.headers.set("Authorization", token);
      config.headers.set("X-Tg-Start-Param", getStartParam());
    }
    const systemOptions = new Intl.DateTimeFormat().resolvedOptions();
    config.headers.set("X-Client-Timezone", systemOptions.timeZone);
    const countries = ct.getTimezone(systemOptions.timeZone)?.countries
    let country = ""
    if (countries && countries.length > 0) {
      country = countries[0];
    }
    config.headers.set("X-Client-Country", country);
    config.headers.set("X-Request-ID", uuidv4());
    config.headers.set("X-Public-Base-URL", host);
    return config;
  },
  (error) => {
    logError(error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    if (!error.response || error.response.status >= 500) {
      return retryRequest(error, 3);
    } else {
      logError(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
